const ESCAPES: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

export function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (ch) => ESCAPES[ch]);
}

function emphasize(text: string): string {
  return escapeHtml(text)
    .replace(/\*\*([^*\n]+)\*\*|__([^_\n]+)__/g, (_, a?: string, b?: string) => `<strong>${a ?? b}</strong>`)
    .replace(/(^|[^\w*])\*([^*\s][^*\n]*?)\*(?!\w)/g, "$1<em>$2</em>")
    .replace(/(^|[^\w])_([^_\s][^_\n]*?)_(?!\w)/g, "$1<em>$2</em>");
}

const LINK = /\[([^\]\n]+)\]\((https?:\/\/[^\s)"'<>]+)\)/g;

function formatText(text: string): string {
  let html = "";
  let last = 0;
  for (const match of text.matchAll(LINK)) {
    html += emphasize(text.slice(last, match.index));
    html += `<a href="${escapeHtml(match[2])}" target="_blank" rel="noopener noreferrer">${emphasize(match[1])}</a>`;
    last = match.index! + match[0].length;
  }
  return html + emphasize(text.slice(last));
}

function renderInline(text: string): string {
  return text
    .split(/(`[^`\n]+`)/g)
    .map((part) => part.length > 2 && part.startsWith("`") && part.endsWith("`")
      ? `<code>${escapeHtml(part.slice(1, -1))}</code>`
      : formatText(part))
    .join("");
}

const FENCE = /^\s*(`{3,}|~{3,})/;
const HEADING = /^\s{0,3}(#{1,6})\s+(.*?)\s*#*\s*$/;
const BULLET = /^\s*[-*+]\s+(.*)$/;
const ORDERED = /^\s*(\d{1,9})[.)]\s+(.*)$/;
const QUOTE = /^\s*>\s?(.*)$/;

export function renderMarkdown(source: string): string {
  const lines = source.replace(/\r\n?/g, "\n").split("\n");
  const blocks: string[] = [];
  let paragraph: string[] = [];

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    blocks.push(`<p>${paragraph.map(renderInline).join("<br>")}</p>`);
    paragraph = [];
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const fence = line.match(FENCE);
    if (fence) {
      flushParagraph();
      const marker = fence[1];
      const code: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trimStart().startsWith(marker)) {
        code.push(lines[i]);
        i++;
      }
      blocks.push(`<pre><code>${escapeHtml(code.join("\n"))}</code></pre>`);
      continue;
    }
    if (!line.trim()) {
      flushParagraph();
      continue;
    }
    const heading = line.match(HEADING);
    if (heading) {
      flushParagraph();
      const level = Math.min(heading[1].length, 4);
      blocks.push(`<h${level}>${renderInline(heading[2])}</h${level}>`);
      continue;
    }
    const bullet = line.match(BULLET);
    const ordered = bullet ? null : line.match(ORDERED);
    if (bullet || ordered) {
      flushParagraph();
      const tag = bullet ? "ul" : "ol";
      const pattern = bullet ? BULLET : ORDERED;
      const start = ordered && ordered[1] !== "1" ? ` start="${Number(ordered[1])}"` : "";
      const items: string[] = [];
      while (i < lines.length) {
        const match = lines[i].match(pattern);
        if (match) {
          items.push(renderInline(bullet ? match[1] : match[2]));
        } else if (lines[i].trim() && /^\s{2,}\S/.test(lines[i]) && items.length > 0) {
          items[items.length - 1] += `<br>${renderInline(lines[i].trim())}`;
        } else {
          break;
        }
        i++;
      }
      i--;
      blocks.push(`<${tag}${start}>${items.map((item) => `<li>${item}</li>`).join("")}</${tag}>`);
      continue;
    }
    const quote = line.match(QUOTE);
    if (quote) {
      flushParagraph();
      const quoted: string[] = [];
      while (i < lines.length) {
        const match = lines[i].match(QUOTE);
        if (!match) break;
        quoted.push(renderInline(match[1]));
        i++;
      }
      i--;
      blocks.push(`<blockquote>${quoted.join("<br>")}</blockquote>`);
      continue;
    }
    paragraph.push(line.trim());
  }
  flushParagraph();
  return blocks.join("");
}
